/**
 * API keys management page
 */

import { KeyIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useGetApiKeysQuery, useDeleteApiKeyMutation } from '@/services/api';

export default function ApiKeys() {
  const { data: apiKeys, isLoading } = useGetApiKeysQuery();
  const [deleteApiKey] = useDeleteApiKeyMutation();

  return (
    <div className="max-w-4xl space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            API <span className="text-gradient">Keys</span>
          </h1>
          <p className="text-gray-400 mt-2">
            Manage API keys for programmatic access to the Patch Manager API
          </p>
        </div>
        <button className="btn-primary flex items-center gap-2">
          <PlusIcon className="w-5 h-5" />
          Create Key
        </button>
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold mb-6">Your API Keys</h2>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : !apiKeys || apiKeys.length === 0 ? (
          <div className="text-center py-12 bg-dark-bg-tertiary rounded-lg">
            <KeyIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No API keys yet</h3>
            <p className="text-gray-400 mb-4">Create your first API key to get started</p>
            <button className="btn-primary">Create API Key</button>
          </div>
        ) : (
          <div className="space-y-4">
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="bg-dark-bg-tertiary rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold">{key.key_name}</h3>
                    <span
                      className={`badge ${key.is_active ? 'badge-info' : 'bg-gray-600 text-gray-300'}`}
                    >
                      {key.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-400">
                    <span className="font-mono">{key.key_prefix}...</span>
                    {key.last_used_at && (
                      <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                    )}
                    {key.expires_at && (
                      <span>Expires: {new Date(key.expires_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => deleteApiKey(key.id)}
                  className="p-2 text-severity-critical hover:bg-severity-critical/10 rounded-lg transition-colors"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="text-xl font-semibold mb-4">API Documentation</h2>
        <p className="text-gray-400 mb-4">
          Learn how to use the Patch Manager API in your applications.
        </p>
        <a href="#" className="link">
          View API Documentation →
        </a>
      </div>
    </div>
  );
}
